import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { getAvatarSrc } from "../lib/avatar";
import { getPaymentProofSrc } from "../lib/paymentProof";

export default function Success() {
  const { id } = useParams();
  const loc = useLocation();
  const [member, setMember] = useState(loc.state?.member || null);
  const [meta, setMeta] = useState(null);
  const proofPreview = loc.state?.proofPreview || "";

  useEffect(() => {
    if (!member)
      api.get(`/membership/${id}`).then(({ data }) => setMember(data));
    api.get("/meta").then(({ data }) => setMeta(data));
  }, [id, member]);

  if (!member || !meta) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  const rawId = member?.id || id || "";

  const shortRef = rawId ? `OSPF-${rawId.slice(-4).toUpperCase()}` : "Pending";

  const payableAmount =
    Number(member?.amount) > 0
      ? member.amount
      : member?.residence_type === "indian"
        ? 2100
        : 1100;

  const referenceId = member?.id || id || "Pending";
  const proofSrc = getPaymentProofSrc(member);

  const waText = encodeURIComponent(
    `Namaste 🙏

I'm ${member?.name || "Member"}.

I've submitted my membership application for Oswal Sabha Professional Forum.

Plan: ${member?.residence_type === "indian" ? "Indian Resident" : "NRI"}
Amount: ₹${payableAmount}
Reference No: ${shortRef}

My form is saved and payment proof has been uploaded.

Please review and contact me when convenient.

Regards,
${member?.name || "Member"}`,
  );

  const waAdminText = encodeURIComponent(
    `Your membership request has been received successfully.

Reference No: ${shortRef}

Our team will contact you shortly.

— Oswal Sabha Professional Forum`,
  );

  const waUrl = `https://wa.me/${meta.whatsapp_number}?text=${waText}`;
  const waSelfUrl = `https://wa.me/?text=${waAdminText}`;

  return (
    <div
      className="container-px pt-14 md:pt-20 pb-24"
      data-testid="success-page"
    >
      <div className="max-w-2xl mx-auto text-center">
        <div className="flex justify-center mb-6">
          <BrandLogo className="justify-center" />
        </div>
        <div
          className="mx-auto w-20 h-20 rounded-full bg-green-100 text-green-700 flex items-center justify-center animate-scale-in"
          data-testid="success-check"
        >
          <Check className="w-10 h-10" strokeWidth={2} />
        </div>
        <div className="overline mt-8 text-green-700">
          Final submit complete
        </div>
        <h1 className="mt-4 font-heading text-4xl md:text-5xl font-light text-ink leading-tight">
          Membership application received
        </h1>
        <p className="mt-5 text-ink-soft leading-relaxed max-w-xl mx-auto">
          Your form has been saved and the payment proof has been received. The
          council will review your application shortly — you'll be notified on
          email and WhatsApp once a decision is made.
        </p>

        <div
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 text-green-800 text-sm"
          data-testid="payment-status-badge"
        >
          <span className="w-2 h-2 rounded-full bg-green-600" />
          Proof submitted · Ref #OSPF-{rawId.slice(0, 8).toUpperCase()}
        </div>
        {member.membership_number ? (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 text-brand text-sm">
            Membership ID · {member.membership_number}
          </div>
        ) : null}
      </div>

      <div
        className="max-w-2xl mx-auto mt-12 card-premium"
        data-testid="member-summary"
      >
        <div className="overline mb-4">Your application summary</div>
        {getAvatarSrc(member) && (
          <div className="mb-5 rounded-2xl overflow-hidden border border-black/5 bg-white">
            <img
              src={getAvatarSrc(member)}
              alt={member.name}
              className="w-full h-72 object-contain bg-white"
              data-testid="success-photo-preview"
            />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 text-sm">
          <Row k="Name" v={member.name} />
          <Row k="Email" v={member.email} />
          <Row k="Phone" v={member.phone} />
          <Row k="City" v={member.city || "—"} />
          <Row k="Profession" v={member.profession} />
          <Row
            k="Residence"
            v={member.residence_type === "indian" ? "Indian Resident" : "NRI"}
          />
          <Row k="Plan amount" v={`₹${member.amount}`} />
          <Row k="Transaction ID" v={member.transaction_id || "—"} />
          <Row
            k="Status"
            v={
              member.payment_status === "proof_submitted"
                ? "Pending council review"
                : "Pending"
            }
          />
        </div>
        <div className="mt-4 border-t border-black/5 pt-4 text-sm">
          <div className="text-ink-muted mb-1">Business details</div>
          <div className="text-ink">{member.business_details}</div>
        </div>

        {(proofSrc || member.payment_proof_path) && (
          <div className="mt-5 rounded-2xl border border-black/5 overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-black/5 text-sm font-medium text-ink">
              Payment proof preview
            </div>
            {proofSrc ? (
              <img
                src={proofSrc}
                alt="Payment proof"
                className="w-full max-h-[420px] object-contain bg-cream-deep"
                data-testid="success-proof-preview"
              />
            ) : (
              <div className="p-5 text-sm text-ink-muted">
                Payment proof is saved and will be available to council review.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to={`/membership/print/${id}`}
          state={{ member }}
          className="btn-secondary"
        >
          Print application PDF
        </Link>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp hover:bg-whatsapp-hover text-white px-7 py-3 font-medium shadow-premium transition"
          data-testid="whatsapp-confirm-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.81 11.81 0 0 1 8.412 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z" />
          </svg>
          Send Application on WhatsApp
        </a>
        <a
          href={waSelfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary"
          data-testid="whatsapp-share-btn"
        >
          Share receipt
        </a>
      </div>

      <div className="text-center mt-10">
        <Link to="/" className="btn-ghost" data-testid="success-home-link">
          Back to home <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col">
      <span className="text-ink-muted">{k}</span>
      <span className="text-ink font-medium break-words">{v}</span>
    </div>
  );
}
